/**
 * useSummary — summary fetch hook for AIFileUploadV2 (AC-2.7).
 *
 * Design decisions:
 *  - T5-34: AbortController on fetch.
 *  - T2-5: On 410 response, navigate to /renter/home.
 *  - T5-12: useTransition on refetch so UI stays responsive.
 */
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  getSummary,
  patchMatch,
  applyAll as applyAllService,
} from 'src/services/aiFileUploadV2';
import type {
  ApplyResult,
  ExtractionMatch,
  SummaryResponse,
} from 'src/types/aiFileUploadV2';

export interface UseSummaryResult {
  summary: SummaryResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  confirmMatch: (
    fileId: number,
    matchId: number,
    value: string
  ) => Promise<ExtractionMatch | null>;
  applyAll: () => Promise<ApplyResult | null>;
  isPending: boolean;
}

export function useSummary(
  surveyId: number | null,
  targetAnswerGroupId?: number,
  onApplied?: () => void,
  targetQitId?: number | null
): UseSummaryResult {
  const navigate = useNavigate();
  const location = useLocation();
  // Base path: strip /review suffix so 410 redirect goes back to the upload page
  // Works for both /renter/files/review and /renter/certification/x/files/y/review
  const uploadPagePathRef = useRef(location.pathname.replace(/\/review$/, ''));
  uploadPagePathRef.current = location.pathname.replace(/\/review$/, '');
  // ENG-2801: keep onApplied in a ref so doFetch's identity stays stable —
  // changing callback identity must not trigger an abort/refetch loop.
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const abortRef = useRef<AbortController | null>(null);
  // Tracks whether we have received at least one successful response.
  // Used to decide whether doFetch should show the full-page spinner (first
  // load) or run silently in the background (subsequent refetches, e.g. after
  // a conflict-picker patch). Using a ref avoids adding `summary` as a
  // dependency of doFetch and avoids the React anti-pattern of calling
  // setState inside a setState updater function.
  const hasLoadedRef = useRef(false);

  const doFetch = useCallback(() => {
    if (!surveyId) {
      // Do NOT set loading=false here. The context is still initialising and
      // surveyId will become non-null shortly. Setting loading=false now
      // causes a brief "No insights" empty-state flash before the real fetch
      // starts (loading=false + summary=null → empty screen → loading=true →
      // data). Keeping loading=true suppresses that flash.
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    // Show the full-page spinner only on the first load. Subsequent refetches
    // (e.g. triggered after a conflict-picker patch) run silently — the
    // existing summary stays displayed and swaps in when the new response
    // arrives. This prevents the page from flashing back to a spinner on
    // every conflict pick.
    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    getSummary(surveyId, signal, targetAnswerGroupId, targetQitId)
      .then((data) => {
        hasLoadedRef.current = true;
        setSummary(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const e = err as {
          name?: string;
          message?: string;
          status?: number;
          code?: string;
          response?: { status?: number };
        };
        // Axios's response interceptor transforms CanceledError/AbortError
        // into the plain string 'canceled' before rejecting, losing the
        // .name property. We must check both the Error-object form and the
        // transformed-string form so that aborted fetches (e.g. from rapid
        // successive refetch() calls) are silently ignored.
        if (
          e.name === 'CanceledError' ||
          e.name === 'AbortError' ||
          (err as string) === 'canceled'
        ) {
          return;
        }
        // T2-5 / ENG-2801: 410 means session already applied. The axios
        // interceptor (src/utils/axios.tsx) flattens errors to
        // { ...response.data, status }, so the HTTP status is a TOP-LEVEL
        // field — NOT e.response.status. Read both for safety and also
        // accept the explicit body code.
        const httpStatus = e.status ?? e.response?.status;
        if (httpStatus === 410 || e.code === 'summary_applied') {
          // ENG-2801: in the interview embed the review screen is a portal
          // (no /review route), so route navigation is a no-op. When an
          // onApplied handler is supplied, "already applied" means the
          // question is done — advance the interview instead of navigating.
          if (onAppliedRef.current) {
            onAppliedRef.current();
          } else {
            navigate(uploadPagePathRef.current, { replace: true });
          }
          return;
        }
        setError(e.message ?? 'Failed to load summary');
        setLoading(false);
      });
  }, [surveyId, targetAnswerGroupId, targetQitId, navigate]);

  useEffect(() => {
    doFetch();
    return () => {
      abortRef.current?.abort();
    };
  }, [doFetch]);

  // T5-12: wrap refetch in transition so UI stays responsive
  const refetch = useCallback(() => {
    startTransition(() => {
      doFetch();
    });
  }, [doFetch, startTransition]);

  const confirmMatch = useCallback(
    async (
      fileId: number,
      matchId: number,
      value: string
    ): Promise<ExtractionMatch | null> => {
      const ac = new AbortController();
      try {
        const updated = await patchMatch(
          fileId,
          matchId,
          { value, user_confirmed: true },
          ac.signal
        );
        return updated;
      } catch (err: unknown) {
        const e = err as Error;
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          setError(e.message ?? 'Failed to confirm match');
        }
        return null;
      }
    },
    []
  );

  const applyAll = useCallback(async (): Promise<ApplyResult | null> => {
    if (!surveyId) {
      return null;
    }
    const ac = new AbortController();
    try {
      const result = await applyAllService(
        surveyId,
        ac.signal,
        targetAnswerGroupId
      );
      return result;
    } catch (err: unknown) {
      const e = err as Error;
      if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
        setError(e.message ?? 'Failed to apply all');
      }
      return null;
    }
  }, [surveyId, targetAnswerGroupId]);

  return {
    summary,
    loading,
    error,
    refetch,
    confirmMatch,
    applyAll,
    isPending,
  };
}
